import { useEffect, useMemo, useRef, useState } from "react";
import UserDrawer from "../components/UserDrawer";
import ConfirmDialog from "../components/ConfirmDialog";
import { createUser, deleteUser, listUsers, resetUserPassword, updateUser } from "../api/users";
import {
  COMPANY_INFO_EVENT,
  DEFAULT_COMPANY_INFO,
  getSavedCompanyInfo,
  saveCompanyInfo,
} from "../utils/companyInfo";

const ACCESS_OPTIONS = [
  { key: "inventory", label: "Inventory", helper: "Stock, pricing, and expirations" },
  { key: "invoices", label: "Invoices", helper: "Issue, settle, and export invoices" },
  { key: "suppliers", label: "Suppliers", helper: "Onboard suppliers and edit contacts" },
  { key: "customers", label: "Customers", helper: "Customer accounts and credit notes" },
  { key: "reports", label: "Reports", helper: "Dashboards, exports, and scheduled sends" },
  { key: "employees", label: "Employees", helper: "Invite teammates and manage their access" },
  { key: "settings", label: "Settings", helper: "Organization preferences" },
];

const HIDDEN_ELIGIBILITY_LABELS = {
  users: "Users",
  notifications: "Notifications",
};

const ADMIN_PANEL_URL = "https://admin.highwindow-kwt.com";
const DEFAULT_ANNUAL_LEAVE_DAYS = 30;
const DEFAULT_SICK_LEAVE_DAYS = 14;

const getInitials = (name = "") =>
  name
    .split(" ")
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase() || "👤";

const buildEmailPreview = ({ email, name, password, reason = "invite" }) => {
  const subject = reason === "reset" ? "Your password was reset" : "You have access to High Window";
  const greetingName = name || "there";
  const bodyLines = [
    `Hi ${greetingName},`,
    reason === "reset"
      ? "Here is your new password to access the admin panel."
      : "You've been invited to the High Window admin panel.",
    "",
    `Login: ${email}`,
    `Password: ${password}`,
    `Link: ${ADMIN_PANEL_URL}`,
    "",
    "This password was generated automatically. Please change it after signing in.",
  ];

  return {
    to: email,
    subject,
    body: bodyLines.join("\n"),
    password,
    link: ADMIN_PANEL_URL,
    reason,
  };
};

function SettingsPage({
  currentUserEmail,
  onDeactivateCurrentUser,
  openDrawerSignal = 0,
  showCompanyPanel = true,
  extraPanels = null,
  canManageUsers = true,
  leaveUsageById = null,
  showLeaveUsage = false,
  hideMainContent = false,
}) {
  const shouldShowCompanyPanel = showCompanyPanel !== false;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [savingUser, setSavingUser] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [formError, setFormError] = useState("");
  const [notice, setNotice] = useState("");
  const [emailPreview, setEmailPreview] = useState(null);
  const [resettingId, setResettingId] = useState("");
  const [deletingId, setDeletingId] = useState("");
  const [copiedPassword, setCopiedPassword] = useState(false);
  const [companyForm, setCompanyForm] = useState(() => getSavedCompanyInfo());
  const [companySaving, setCompanySaving] = useState(false);
  const [companyNotice, setCompanyNotice] = useState("");
  const [companyError, setCompanyError] = useState("");
  const lastOpenSignal = useRef(openDrawerSignal);
  const [drawerMode, setDrawerMode] = useState("create");
  const [selectedUser, setSelectedUser] = useState(null);
  const [confirmingUser, setConfirmingUser] = useState(null);

  const accessLookup = useMemo(() => {
    const map = new Map();
    ACCESS_OPTIONS.forEach((opt) => map.set(opt.key, opt.label));
    Object.entries(HIDDEN_ELIGIBILITY_LABELS).forEach(([key, label]) => map.set(key, label));
    return map;
  }, []);

  const leaveUsageLookup = useMemo(() => {
    if (!showLeaveUsage || !leaveUsageById) return new Map();
    if (leaveUsageById instanceof Map) return leaveUsageById;
    try {
      return new Map(Object.entries(leaveUsageById || {}));
    } catch (_err) {
      return new Map();
    }
  }, [leaveUsageById, showLeaveUsage]);

  const showProfileColumns = !canManageUsers && showLeaveUsage;

  const currentUser = useMemo(() => {
    if (!users.length) return null;
    const normalizedEmail = (currentUserEmail || "").toLowerCase().trim();
    if (normalizedEmail) {
      const match = users.find((u) => (u.email || "").toLowerCase().trim() === normalizedEmail);
      if (match) return match;
    }
    return users[0] || null;
  }, [currentUserEmail, users]);

  const currentLeaveStats = useMemo(() => {
    if (!showProfileColumns) return null;
    const id = currentUser?.id;
    if (id == null) return null;
    return leaveUsageLookup.get(String(id)) || leaveUsageLookup.get(id) || null;
  }, [currentUser, leaveUsageLookup, showProfileColumns]);

  useEffect(() => {
    let cancelled = false;
    const fetchUsers = async () => {
      setUsersLoading(true);
      setFormError("");
      try {
        const data = await listUsers();
        if (!cancelled) {
          const all = Array.isArray(data) ? data : [];
          if (canManageUsers) {
            setUsers(all);
          } else {
            const normalized = currentUserEmail?.toLowerCase().trim();
            const selfOnly = normalized ? all.filter((u) => (u.email || "").toLowerCase().trim() === normalized) : [];
            setUsers(selfOnly);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setFormError(err?.message || "Unable to load users.");
        }
      } finally {
        if (!cancelled) {
          setUsersLoading(false);
        }
      }
    };

    fetchUsers();
    return () => {
      cancelled = true;
    };
  }, [canManageUsers, currentUserEmail]);

  useEffect(() => {
    if (!canManageUsers) return;
    if (openDrawerSignal !== lastOpenSignal.current) {
      setDrawerMode("create");
      setSelectedUser(null);
      setDrawerOpen(true);
      lastOpenSignal.current = openDrawerSignal;
    }
  }, [openDrawerSignal, canManageUsers]);

  useEffect(() => {
    if (!shouldShowCompanyPanel) return undefined;
    if (typeof window === "undefined") return undefined;
    const syncCompanyInfo = () => {
      setCompanyForm(getSavedCompanyInfo());
    };
    window.addEventListener("storage", syncCompanyInfo);
    window.addEventListener(COMPANY_INFO_EVENT, syncCompanyInfo);
    return () => {
      window.removeEventListener("storage", syncCompanyInfo);
      window.removeEventListener(COMPANY_INFO_EVENT, syncCompanyInfo);
    };
  }, [shouldShowCompanyPanel]);

  const handleToggleUserActive = async (userId) => {
    if (!canManageUsers) return;
    const targetUser = users.find((user) => user.id === userId);
    if (!targetUser) return;

    setFormError("");
    setNotice("");
    const nextActive = targetUser.active === false;

    try {
      const response = await updateUser(userId, { active: nextActive });
      const updatedUser = response?.user || response;

      setUsers((prev) => prev.map((user) => (user.id === userId ? updatedUser : user)));

      const normalizedCurrentEmail = currentUserEmail?.toLowerCase().trim();
      const normalizedTargetEmail = updatedUser?.email?.toLowerCase().trim();

      if (updatedUser && updatedUser.active === false && normalizedCurrentEmail && normalizedTargetEmail === normalizedCurrentEmail) {
        onDeactivateCurrentUser?.();
      }
    } catch (err) {
      setFormError(err?.message || "Unable to update user status.");
    }
  };

  const handleSubmitUser = async (form) => {
    if (!canManageUsers) return;
    setFormError("");
    setNotice("");
    setSavingUser(true);

    const basePayload = {
      name: form.name.trim(),
      phone: form.phone.trim(),
      eligibility: Array.isArray(form.eligibility) ? Array.from(new Set(form.eligibility.filter(Boolean))) : [],
      role: (form.role || "employee").toLowerCase(),
    };

    if (drawerMode === "edit" && selectedUser?.id) {
      try {
        const response = await updateUser(selectedUser.id, basePayload);
        const updatedUser = response?.user || response;

        setUsers((prev) => prev.map((user) => (user.id === selectedUser.id ? updatedUser : user)));
        setNotice(`Updated ${updatedUser.email || "user"}.`);
        setEmailPreview(null);
        setSelectedUser(null);
        setDrawerMode("create");
        setDrawerOpen(false);
      } catch (err) {
        console.error("Unable to update user", err);
        setFormError(err?.message || "Unable to update user.");
      } finally {
        setSavingUser(false);
      }
      return;
    }

    const payload = {
      ...basePayload,
      email: form.email.trim().toLowerCase(),
    };

    try {
      const response = await createUser(payload);
      const user = response?.user || response;
      const password = response?.password;
      const emailStatus = response?.emailStatus;

      setUsers((prev) => [user, ...prev]);

      if (password) {
        setEmailPreview(buildEmailPreview({ email: user.email, name: user.name, password, reason: "invite" }));
      } else {
        setEmailPreview(null);
      }

      if (emailStatus === "failed") {
        setNotice("User created. Email failed to send—copy the password below.");
      } else {
        setNotice(`Invite ${emailStatus === "sent" ? "sent" : "prepared"} for ${user.email}.`);
      }

      setDrawerOpen(false);
    } catch (err) {
      console.error("Unable to create user", err);
      setFormError(err?.message || "Unable to create user.");
    } finally {
      setSavingUser(false);
    }
  };

  const handleResetPassword = async (userId) => {
    if (!canManageUsers) return;
    const target = users.find((u) => u.id === userId);
    if (!target) return;
    setFormError("");
    setNotice("");
    setResettingId(userId);

    try {
      const response = await resetUserPassword(userId);
      const user = response?.user || target;
      const password = response?.password;
      const emailStatus = response?.emailStatus;

      setUsers((prev) => prev.map((u) => (u.id === userId ? user : u)));

      if (password) {
        setEmailPreview(buildEmailPreview({ email: user.email, name: user.name, password, reason: "reset" }));
      }

      if (emailStatus === "failed") {
        setNotice("Password reset generated. Email failed to send—copy the password below.");
      } else {
        setNotice(`Password reset ${emailStatus === "sent" ? "sent" : "prepared"} for ${user.email}.`);
      }
    } catch (err) {
      console.error("Unable to reset password", err);
      setFormError(err?.message || "Unable to reset password.");
    } finally {
      setResettingId("");
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!canManageUsers) return;
    const target = users.find((u) => u.id === userId);
    if (!target) return;

    setFormError("");
    setNotice("");
    setDeletingId(userId);

    try {
      await deleteUser(userId);

      setConfirmingUser(null);

      if (selectedUser?.id === userId) {
        handleCloseDrawer();
      }

      setUsers((prev) => prev.filter((user) => user.id !== userId));

      const normalizedCurrentEmail = currentUserEmail?.toLowerCase().trim();
      const normalizedTargetEmail = target.email?.toLowerCase().trim();

      if (normalizedCurrentEmail && normalizedTargetEmail && normalizedCurrentEmail === normalizedTargetEmail) {
        onDeactivateCurrentUser?.();
      }

      setNotice(`Removed ${target.email || "user"}.`);
    } catch (err) {
      console.error("Unable to delete user", err);
      setFormError(err?.message || "Unable to delete user.");
    } finally {
      setDeletingId("");
    }
  };

const pendingUserEmail = confirmingUser?.email || "";

const handleEditUser = (user) => {
  if (!canManageUsers) return;
  setSelectedUser(user);
  setDrawerMode("edit");
  setEmailPreview(null);
  setNotice("");
  setFormError("");
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setFormError("");
    setSelectedUser(null);
    setDrawerMode("create");
    setDrawerOpen(false);
  };

  const formatEligibilityLabel = (key) => accessLookup.get(key) || key;
  const formatRoleLabel = (role) => {
    const normalized = String(role || "").toLowerCase();
    if (normalized === "super admin" || normalized === "super_admin") return "Super Admin";
    if (normalized === "admin") return "Admin";
    return "Employee";
  };

  const handleCompanyFieldChange = (field) => (event) => {
    const value = event.target.value;
    setCompanyForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveCompanyDetails = (event) => {
    event.preventDefault();
    setCompanyError("");
    setCompanyNotice("");
    setCompanySaving(true);
    const success = saveCompanyInfo(companyForm);
    setCompanySaving(false);
    if (success) {
      setCompanyForm(getSavedCompanyInfo());
      setCompanyNotice("Company information saved.");
    } else {
      setCompanyError("Unable to save company information. Check browser storage settings.");
    }
  };

  const accessPanelContent = (
    <article className="panel access-panel">
      <header className="panel-header">
        <div>
          <h2>Platform access</h2>
          <p className="muted small">Invite teammates and choose which tabs they can see after signing in.</p>
        </div>
      </header>

      {usersLoading ? (
        <div className="inline-alert info compact">
          <span>Loading users...</span>
        </div>
      ) : null}
      {formError ? (
        <div className="inline-alert error compact">
          <span>{formError}</span>
          <button className="ghost-btn small" type="button" onClick={() => setFormError("")}>
            Dismiss
          </button>
        </div>
      ) : null}

      {notice ? (
        <div className="inline-alert success compact">
          <span>{notice}</span>
          <button className="ghost-btn small" type="button" onClick={() => setNotice("")}>
            Dismiss
          </button>
        </div>
      ) : null}
      {emailPreview ? (
        <div className="email-preview" aria-label="Email preview">
          <div className="email-preview__head">
            <div>
              <div className="pill subtle small">{emailPreview.reason === "reset" ? "Password reset" : "Invite"}</div>
              <div className="email-preview__subject">{emailPreview.subject}</div>
              <div className="muted small">To: {emailPreview.to}</div>
            </div>
            <button className="ghost-btn small" type="button" onClick={async () => {
              if (!emailPreview?.password) return;
              if (!navigator?.clipboard?.writeText) {
                console.warn("Clipboard API not available");
                return;
              }
              try {
                await navigator.clipboard.writeText(emailPreview.password);
                setCopiedPassword(true);
                setTimeout(() => setCopiedPassword(false), 1200);
              } catch (err) {
                console.error("Unable to copy password", err);
              }
            }}>
              {copiedPassword ? "Copied" : "Copy password"}
            </button>
          </div>
          <div className="email-credentials">
            <div className="email-cred-card">
              <div className="muted extra-small">Login</div>
              <div className="email-cred-value">{emailPreview.to}</div>
            </div>
            <div className="email-cred-card accent">
              <div className="muted extra-small">Password</div>
              <div className="email-cred-value">{emailPreview.password}</div>
            </div>
            <div className="email-cred-card">
              <div className="muted extra-small">Admin link</div>
              <div className="email-cred-value">{emailPreview.link}</div>
            </div>
          </div>
          <div className="email-preview__body">
            {emailPreview.body.split("\n").map((line, idx) => (
              <p key={idx} className="email-line">
                {line || <>&nbsp;</>}
              </p>
            ))}
          </div>
        </div>
      ) : null}

      <div className="access-grid">
        {users.length ? (
          users.map((user) => {
            const normalizedEmail = user.email?.toLowerCase().trim();
            const isProtectedAdmin = user.role === "Super Admin" || normalizedEmail === "admin@hwms.com";
            return (
              <article
                key={user.id}
                className="access-card"
                role={isProtectedAdmin || !canManageUsers ? undefined : "button"}
                tabIndex={isProtectedAdmin || !canManageUsers ? -1 : 0}
                aria-disabled={isProtectedAdmin || !canManageUsers}
                onClick={() => {
                  if (isProtectedAdmin || !canManageUsers) return;
                  handleEditUser(user);
                }}
                onKeyDown={(event) => {
                  if (isProtectedAdmin || !canManageUsers) return;
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    handleEditUser(user);
                  }
                }}
              >
                    <div className="access-card__head">
                      <div className="access-avatar">{getInitials(user.name)}</div>
                      <div className="access-meta">
                        <div className="access-name">{user.name || "Unnamed user"}</div>
                        <div className="pill subtle small">{formatRoleLabel(user.role)}</div>
                        <div className="muted small">{user.email}</div>
                      </div>
                      <div className="access-status">
                        <span className={`pill status small ${user.active === false ? "danger" : "success"}`}>
                          {user.active === false ? "Inactive" : "Allowed"}
                        </span>
                        {!isProtectedAdmin && canManageUsers ? (
                          <button
                            className={`access-toggle ${user.active === false ? "off" : "on"}`}
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleToggleUserActive(user.id);
                            }}
                            aria-pressed={user.active !== false}
                          >
                            <span className="access-toggle__track">
                              <span className="access-toggle__thumb" />
                            </span>
                            <span className="access-toggle__text">{user.active === false ? "Inactive" : "Active"}</span>
                          </button>
                        ) : null}
                        {!isProtectedAdmin && canManageUsers ? (
                          <button
                            className="access-delete-btn"
                            type="button"
                            title="Remove user"
                            aria-label={`Remove ${user.email || "user"}`}
                            aria-busy={deletingId === user.id}
                            disabled={deletingId === user.id}
                            onClick={(event) => {
                              event.stopPropagation();
                              setConfirmingUser(user);
                            }}
                          >
                            {deletingId === user.id ? (
                              <span className="access-delete-spinner" aria-hidden="true">...</span>
                            ) : (
                              <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M9 3.5a1 1 0 011-1h4a1 1 0 011 1V5h3.5a1 1 0 110 2h-.94l-.8 12.13A2 2 0 0113.78 21H10.2a2 2 0 01-1.99-1.87L7.4 7H6a1 1 0 110-2H9V3.5zm2 1.5v0.99h2V5h-2zm-1.07 4a1 1 0 011 .94l.38 6.5a1 1 0 11-2 .12l-.38-6.5a1 1 0 01.94-1.06zm4.22 0a1 1 0 01.94 1.06l-.38 6.5a1 1 0 11-2-.12l.38-6.5a1 1 0 011-.94z" />
                              </svg>
                            )}
                          </button>
                        ) : null}
                      </div>
                    </div>
                <div className="muted small">{user.phone || "No phone provided"}</div>
                <div className="access-eligibility">
                  {user.eligibility?.length ? (
                    user.eligibility.map((key) => (
                      <span key={key} className="pill status">
                        {formatEligibilityLabel(key)}
                      </span>
                    ))
                  ) : (
                    <span className="muted extra-small">No tabs assigned yet.</span>
                  )}
                </div>
                <div className="access-actions">
                  {canManageUsers ? (
                    <button
                      className="ghost-btn small"
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleResetPassword(user.id);
                      }}
                      disabled={resettingId === user.id}
                    >
                      {resettingId === user.id ? "Sending..." : "Reset password"}
                    </button>
                  ) : null}
                  {user.lastEmailAt ? (
                    <span className="muted extra-small">
                      Last {user.lastEmailType === "reset" ? "reset" : "invite"}:{" "}
                      {new Date(user.lastEmailAt).toLocaleString()}
                    </span>
                  ) : null}
                </div>
              </article>
            );
          })
        ) : usersLoading ? null : (
          <div className="inline-alert compact info">
            <span>No users yet. Create the first teammate to send them a login.</span>
          </div>
        )}
      </div>
    </article>
  );

  const companyPanelContent = shouldShowCompanyPanel ? (
    <article className="panel company-panel">
      <header className="panel-header">
        <div className="panel-title">
          <div>
            <h2>Company details</h2>
            <p className="muted small">Shown on invoices beneath the logo.</p>
          </div>
        </div>
      </header>
      <form id="company-info-form" className="company-info-form" onSubmit={handleSaveCompanyDetails} noValidate>
        <div className="company-info-top">
          {companyNotice ? (
            <div className="inline-alert success compact">
              <span>{companyNotice}</span>
              <button className="ghost-btn small" type="button" onClick={() => setCompanyNotice("")}>
                Dismiss
              </button>
            </div>
          ) : null}
          {companyError ? (
            <div className="inline-alert error compact">
              <span>{companyError}</span>
              <button className="ghost-btn small" type="button" onClick={() => setCompanyError("")}>
                Dismiss
              </button>
            </div>
          ) : null}
          <p className="muted small">Keep this up to date so invoices mirror your real-world letterhead.</p>
        </div>
        <div className="company-info-grid">
          <div className="field full-span">
            <label className="field-label" htmlFor="company-name">
              Company name
            </label>
            <input
              id="company-name"
              type="text"
              placeholder={DEFAULT_COMPANY_INFO.name}
              value={companyForm.name}
              onChange={handleCompanyFieldChange("name")}
              required
            />
          </div>
          <div className="field full-span">
            <div className="field-top">
              <label className="field-label" htmlFor="company-address">
                Address
              </label>
              <span className="muted extra-small">Separate multiple lines with line breaks.</span>
            </div>
            <textarea
              id="company-address"
              placeholder={DEFAULT_COMPANY_INFO.address}
              value={companyForm.address}
              onChange={handleCompanyFieldChange("address")}
              rows={4}
            />
          </div>
          <div className="field">
            <label className="field-label" htmlFor="company-email">
              Email
            </label>
            <input
              id="company-email"
              type="email"
              placeholder={DEFAULT_COMPANY_INFO.email}
              value={companyForm.email}
              onChange={handleCompanyFieldChange("email")}
            />
          </div>
          <div className="field">
            <label className="field-label" htmlFor="company-phone">
              Phone number
            </label>
            <input
              id="company-phone"
              type="text"
              placeholder={DEFAULT_COMPANY_INFO.phone}
              value={companyForm.phone}
              onChange={handleCompanyFieldChange("phone")}
            />
          </div>
          <div className="company-info-actions full-span">
            <span className="muted small">Need a placeholder? Leave fields empty to fall back to our defaults.</span>
            <button className="primary-btn" type="submit" disabled={companySaving}>
              {companySaving ? "Saving…" : "Save company details"}
            </button>
          </div>
        </div>
      </form>
    </article>
  ) : null;

  const leaveBalancePanelContent = showProfileColumns
    ? (() => {
        const normalizeLeaveCounters = (stats, fallbackTotal) => {
          const parsedTotal = Number(stats?.total);
          const total = !Number.isNaN(parsedTotal) && parsedTotal >= 0 ? Math.round(parsedTotal) : fallbackTotal;
          const parsedUsed = Number(stats?.used);
          const used = !Number.isNaN(parsedUsed) && parsedUsed >= 0 ? Math.round(parsedUsed) : 0;
          const parsedRemaining = Number(stats?.remaining);
          const remaining =
            !Number.isNaN(parsedRemaining) && parsedRemaining >= 0
              ? Math.round(parsedRemaining)
              : total != null
                ? Math.max(0, total - used)
                : null;
          return { total, used, remaining };
        };

        const annual = normalizeLeaveCounters(currentLeaveStats?.annual, DEFAULT_ANNUAL_LEAVE_DAYS);
        const sick = normalizeLeaveCounters(currentLeaveStats?.sick, DEFAULT_SICK_LEAVE_DAYS);
        const hasData = currentLeaveStats != null;
        return (
          <article className="panel leave-balance-panel">
            <header className="panel-header">
              <div>
                <h2>Leave balance</h2>
                <p className="muted small">See your approved leave usage and remaining allowance.</p>
              </div>
            </header>
            {hasData ? (
              <div className="leave-balance-grid">
                <div className="leave-balance-card">
                  <div className="muted extra-small">Annual vacation remaining</div>
                  <div className="leave-balance-value accent">
                    {annual.remaining != null ? `${annual.remaining}d` : "—"}
                  </div>
                  <div className="muted extra-small">Out of {annual.total ?? DEFAULT_ANNUAL_LEAVE_DAYS} annual days.</div>
                </div>
                <div className="leave-balance-card">
                  <div className="muted extra-small">Annual vacation used (approved)</div>
                  <div className="leave-balance-value">{annual.used}d</div>
                  <div className="muted extra-small">Approved annual leave days taken.</div>
                </div>
                <div className="leave-balance-card">
                  <div className="muted extra-small">Sick leave remaining</div>
                  <div className="leave-balance-value accent">
                    {sick.remaining != null ? `${sick.remaining}d` : "—"}
                  </div>
                  <div className="muted extra-small">Out of {sick.total ?? DEFAULT_SICK_LEAVE_DAYS} sick leave days.</div>
                </div>
                <div className="leave-balance-card">
                  <div className="muted extra-small">Sick leave used (approved)</div>
                  <div className="leave-balance-value">{sick.used}d</div>
                  <div className="muted extra-small">Approved sick leave days taken.</div>
                </div>
              </div>
            ) : (
              <div className="inline-alert compact info">
                <span>No leave data yet.</span>
              </div>
            )}
          </article>
        );
      })()
    : null;

  const sectionStyle = hideMainContent ? { display: "none" } : undefined;
  const sectionAriaHidden = hideMainContent;
  const settingsGridClassName = `settings-grid settings-grid--settings${showProfileColumns ? " profile-columns profile-layout" : ""}`;
  const stackedGridClassName = `settings-grid${showProfileColumns ? " profile-layout" : ""}`;

  return (
    <>
      <section
        className={settingsGridClassName}
        style={sectionStyle}
        aria-hidden={sectionAriaHidden}
      >
        {accessPanelContent}
        {showProfileColumns ? leaveBalancePanelContent : null}
        {!showProfileColumns ? companyPanelContent : null}
        {!showProfileColumns ? extraPanels : null}
      </section>

      {showProfileColumns && companyPanelContent ? (
        <section className={stackedGridClassName} style={sectionStyle} aria-hidden={sectionAriaHidden}>
          {companyPanelContent}
        </section>
      ) : null}

      {showProfileColumns && extraPanels ? (
        <section className={stackedGridClassName} style={sectionStyle} aria-hidden={sectionAriaHidden}>
          {extraPanels}
        </section>
      ) : null}

      <UserDrawer
        open={drawerOpen}
        onClose={handleCloseDrawer}
        onSubmit={handleSubmitUser}
        saving={savingUser}
        error={formError}
        accessOptions={ACCESS_OPTIONS}
        user={drawerMode === "edit" ? selectedUser : null}
        mode={drawerMode}
        withOverlay
      />
      <ConfirmDialog
        open={Boolean(confirmingUser)}
        title="Remove user?"
        message={
          pendingUserEmail
            ? `Remove ${pendingUserEmail}? They will lose access immediately.`
            : "Remove this user? They will lose access immediately."
        }
        confirmLabel="Remove user"
        cancelLabel="Keep user"
        loading={deletingId === confirmingUser?.id}
        onCancel={() => {
          if (deletingId === confirmingUser?.id) return;
          setConfirmingUser(null);
        }}
        onConfirm={() => confirmingUser?.id && handleDeleteUser(confirmingUser.id)}
      />
    </>
  );
}

export default SettingsPage;
